import type { Warning } from '@tsagko/label-contracts'
import { useCallback, useEffect, useMemo, useState, type Ref } from 'react'
import { useTranslation } from 'react-i18next'
import { PanelCollapseToggle, panelBodyId } from '../ui/CollapsibleSection'
import { PREVIEW_ZPL_PANEL_ID } from './panel-ids'

interface ZplViewerProps {
  zpl: string | null
  warnings: Warning[]
  /**
   * When provided, the header gets a toggle that collapses this column to a
   * rail (#189). The collapsed state lives in PreviewPane, which owns the
   * width the rail hands back to the raster.
   */
  onCollapse?: () => void
  /**
   * PreviewPane's `usePanelCollapsed().toggleRef` for the ZPL panel: this
   * header toggle is unmounted by its own collapse, so it hands focus to the
   * rail's toggle (and takes it back on expand).
   */
  collapseToggleRef?: Ref<HTMLButtonElement>
}

const COPIED_MS = 1_200

// Shared by BOTH header rows below (idle and loaded) so the collapse control
// sits in the same place at the same height whichever state the column is in.
const HEADER_ROW_CLASS =
  'flex items-center gap-2 border-b border-stone-200 px-3 py-1.5 dark:border-stone-800'
const HEADER_LABEL_CLASS =
  'mr-auto text-[11px] font-semibold uppercase tracking-wide text-stone-500'
const COLUMN_CLASS = 'flex h-full min-h-0 flex-col'

export function ZplViewer({ zpl, warnings, onCollapse, collapseToggleRef }: ZplViewerProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const lines = useMemo(() => (zpl ? zpl.split('\n') : []), [zpl])

  useEffect(() => {
    if (!copied) return
    const id = setTimeout(() => setCopied(false), COPIED_MS)
    return () => clearTimeout(id)
  }, [copied])

  const onCopy = useCallback(async () => {
    if (!zpl) return
    try {
      await navigator.clipboard.writeText(zpl)
      setCopied(true)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[ZplViewer] clipboard write failed', e)
    }
  }, [zpl])

  // The collapse control lives ABOVE this early return on purpose (#189
  // review). `zpl` is null on every mount until the first generation lands,
  // and stays null after a first-gen failure — leaving the toggle inside the
  // loaded branch meant the ZPL column, one of the six collapsible regions,
  // had NO collapse affordance in that state, and expanding it back from the
  // persisted rail handed focus to a button that did not exist (focus fell to
  // <body>, with no toggle left to re-collapse with until ZPL arrived). The
  // wrapper carries the same `panelBodyId` in both branches, so the toggle's
  // `aria-controls` resolves whether or not the first ZPL has landed.
  if (!zpl) {
    return (
      <div id={panelBodyId(PREVIEW_ZPL_PANEL_ID)} className={COLUMN_CLASS}>
        {onCollapse && (
          <div data-testid="preview-zpl-idle-header" className={HEADER_ROW_CLASS}>
            <PanelCollapseToggle
              collapsed={false}
              onToggle={onCollapse}
              panelLabel={t('preview.zpl.panelLabel')}
              controls={panelBodyId(PREVIEW_ZPL_PANEL_ID)}
              testId={`${PREVIEW_ZPL_PANEL_ID}-toggle`}
              expandedChevron="right"
              buttonRef={collapseToggleRef}
            />
            {/* No char/line counts and no Copy button while there is nothing
                to count or copy — just the panel name, so the row reads as
                the same header the loaded state shows. */}
            <span className={HEADER_LABEL_CLASS}>{t('preview.zpl.panelLabel')}</span>
          </div>
        )}
        <p className="p-4 text-[11px] text-stone-500 dark:text-stone-400">
          {t('preview.idle')}
        </p>
      </div>
    )
  }

  return (
    <div id={panelBodyId(PREVIEW_ZPL_PANEL_ID)} className={COLUMN_CLASS}>
      <div data-testid="preview-zpl" className={HEADER_ROW_CLASS}>
        {onCollapse && (
          <PanelCollapseToggle
            collapsed={false}
            onToggle={onCollapse}
            panelLabel={t('preview.zpl.panelLabel')}
            controls={panelBodyId(PREVIEW_ZPL_PANEL_ID)}
            testId={`${PREVIEW_ZPL_PANEL_ID}-toggle`}
            expandedChevron="right"
            buttonRef={collapseToggleRef}
          />
        )}
        <span className={HEADER_LABEL_CLASS}>
          {t('preview.zpl.header', { chars: zpl.length, lines: lines.length })}
        </span>
        <button
          type="button"
          data-testid="preview-zpl-copy"
          onClick={onCopy}
          className="rounded border border-stone-300 bg-stone-50 px-2 py-0.5 text-[11px] hover:bg-stone-100 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700"
        >
          {copied ? t('preview.zpl.copied') : t('preview.zpl.copy')}
        </button>
      </div>
      {warnings.length > 0 && (
        <ul className="max-h-32 shrink-0 overflow-y-auto border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          <li className="font-semibold">
            {t('preview.zpl.warnings', { count: warnings.length })}
          </li>
          {warnings.map((w, i) => (
            <li
              key={i}
              data-testid="preview-zpl-warning"
              className="pl-3"
            >
              · {w.message}
            </li>
          ))}
        </ul>
      )}
      <pre className="flex-1 overflow-auto bg-white p-2 font-mono text-[10px] leading-tight text-stone-800 dark:bg-stone-950 dark:text-stone-200">
        {lines.map((line, i) => (
          <div key={i} className="flex">
            <span className="mr-3 w-8 shrink-0 select-none text-right tabular-nums text-stone-400">
              {i + 1}
            </span>
            <span className="whitespace-pre-wrap break-all">{line}</span>
          </div>
        ))}
      </pre>
    </div>
  )
}
